import axios, {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from 'axios';
import { nextTick } from 'vue';

import { __ } from '../stores/expressions';
import { useAppStore } from '../stores/app';
import { useToastStore } from '../stores/snackbar';
import { useValidationErrorsStore, type FieldErrors } from '../stores/validationErrors';
import { passwordPolicyErrorExpressions } from '../consts/password-policy-errors';
import { API_URL, API_VERSION } from '@/config';

interface EchoHttp extends AxiosInstance {
    backoffice: () => BackOfficeHttp;
}

const http = axios.create() as EchoHttp;

let backOfficeApiBaseUrl: string | undefined;
let defaultTenantSlug = '';
let overrideTenantSlug: string | undefined;

const buildTenantBaseUrl = (slug: string) => `${API_URL}/tenant/${slug}/${API_VERSION}`;

export function setBackOfficeApiBaseUrl(url: string | undefined) {
    backOfficeApiBaseUrl = url;
}

export function setOverrideTenantSlug(slug: string | undefined) {
    overrideTenantSlug = slug && slug !== defaultTenantSlug ? slug : undefined;
}

export function getDefaultTenantSlug() {
    return defaultTenantSlug;
}

interface CustomInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
    silent?: boolean;
    request_number?: number;
    errorMessage?: string | ErrorCodesMessage;
    successMessage?: string;
    useBackOffice?: boolean;
    validationScope?: string;
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
    silent?: boolean;
    errorMessage?: string | ErrorCodesMessage;
    successMessage?: string;
    useBackOffice?: boolean;
    validationScope?: string;
}

type ErrorCodesMessage = Record<number, string>;

// Expected 422 payload shape (ASP.NET Core ValidationProblemDetails):
//   { errors: { "field.path": ["expression-key-or-message", ...], ... } }
// Field paths are preserved verbatim and later matched against the
// `validation-key` prop used by validation error consumers.
// This helper only extracts string arrays from the response payload.
// Translation of known expression keys and fallback to raw messages
// happen later in useValidationErrors.
function extractFieldErrors(data: unknown): FieldErrors | null {
    if (!data || typeof data !== 'object') return null;
    const errs = (data as { errors?: unknown }).errors;
    if (!errs || typeof errs !== 'object') return null;
    const out: FieldErrors = {};
    for (const [k, v] of Object.entries(errs)) {
        if (Array.isArray(v) && v.every((x) => typeof x === 'string')) out[k] = v;
    }
    return Object.keys(out).length ? out : null;
}

/**
 * `reason: "PayloadRejected"` means an upstream system refused what the user typed,
 * naming the broken rule in `errorCode` and explaining it in English in `detail`.
 * A known `errorCode` wins because only it speaks the user's language, then `detail`;
 * both beat the call site's status-code fallback.
 */
function buildRejectedPayloadMessage(data: unknown): string | null {
    if (!data || typeof data !== 'object') return null;

    const { reason, detail, errorCode } = data as {
        reason?: unknown;
        detail?: unknown;
        errorCode?: unknown;
    };

    if (reason !== 'PayloadRejected') return null;

    const expression =
        typeof errorCode === 'string' && Object.hasOwn(passwordPolicyErrorExpressions, errorCode)
            ? passwordPolicyErrorExpressions[errorCode]
            : undefined;

    if (expression) return __(expression);

    const text = typeof detail === 'string' ? detail.trim() : '';

    return text ? __('error-payload-rejected', { detail: text }) : null;
}

export function setupHttpConfig(tenantSlug: string, getToken: () => Promise<string>) {
    defaultTenantSlug = tenantSlug;
    http.defaults.withCredentials = true;
    http.defaults.baseURL = buildTenantBaseUrl(tenantSlug);
    http.defaults.headers.common['Content-Type'] = 'application/json';
    http.defaults.headers.common['Accept'] = 'application/json,text/html';
    http.defaults.headers.common['Access-Control-Allow-Origin'] = ['*'];

    http.interceptors.request.use(async (config) => {
        const token = await getToken();

        if (token) {
            config.headers = config.headers ?? {};
            config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
    });
}

let requestNumber = 0;

http.interceptors.request.use(function (config: CustomInternalAxiosRequestConfig) {
    const url = config.url ?? '';
    requestNumber++;

    console.log(String(config.method).toUpperCase() + ' request started', requestNumber, url);

    if (config.data) {
        console.table(config.data);
    }

    if (config.useBackOffice) {
        if (backOfficeApiBaseUrl) {
            config.baseURL = backOfficeApiBaseUrl;
        } else {
            console.warn('Back office API base URL not set; falling back to default baseURL');
        }
    } else if (overrideTenantSlug) {
        config.baseURL = buildTenantBaseUrl(overrideTenantSlug);
    }

    config.request_number = requestNumber;

    if (!config.silent) {
        const appStore = useAppStore();

        appStore.requestStarted(requestNumber, url);
    }

    return config;
});

http.interceptors.response.use(
    function (response: AxiosResponse) {
        const appStore = useAppStore();
        const toast = useToastStore();
        const config = response.config as CustomInternalAxiosRequestConfig;

        console.log('Request ended', config.request_number, config.url);

        if (config.successMessage) {
            toast.addToast('success', config.successMessage);
        }

        if (config.validationScope) {
            useValidationErrorsStore().clearScope(config.validationScope);
        }

        if (!config.silent) {
            appStore.requestEnded(config.request_number ?? 1);
        }

        return response;
    },
    function (error) {
        if (axios.isCancel(error)) {
            const config = error.config as CustomInternalAxiosRequestConfig;
            if (config && !config.silent) {
                const appStore = useAppStore();
                appStore.requestEnded(config.request_number ?? 1);
            }
            return Promise.reject(error);
        }

        const appStore = useAppStore();
        const toast = useToastStore();
        const config = error.config as CustomInternalAxiosRequestConfig | undefined;

        let validationErrorsHandled = false;
        if (error.response?.status === 422 && config?.validationScope) {
            const scope = config.validationScope;
            const fields = extractFieldErrors(error.response.data);
            if (fields) {
                useValidationErrorsStore().setScopeErrors(scope, fields);
                validationErrorsHandled = true;
                nextTick(() => {
                    const scopePrefix = CSS.escape(`${scope}-error-`);
                    const el = document.querySelector<HTMLElement>(`[id^="${scopePrefix}"]`);
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
            }
        }

        const errorMessage = config?.errorMessage;
        const customErrorMessageForStatusCode =
            typeof errorMessage === 'object' ? errorMessage?.[error.status] : undefined;
        const rejectedPayloadMessage = buildRejectedPayloadMessage(error.response?.data);

        if (!config?.silent && !validationErrorsHandled) {
            if (!!rejectedPayloadMessage) {
                toast.addToast('error', rejectedPayloadMessage);
            } else if (!!customErrorMessageForStatusCode) {
                toast.addToast('error', customErrorMessageForStatusCode);
            } else if (error.status === 401) {
                toast.addToast('error', __('error-response-401'));
            } else if (error.status === 403) {
                toast.addToast('error', __('error-response-403'));
            } else if (error.status === 400) {
                toast.addToast('error', __('error-response-400'));
            } else if (error.status === 422) {
                toast.addToast('error', __('error-response-422'));
            } else if (error.status === 500 || !error.status) {
                toast.addToast('error', __('error-response'));
            }
        }

        if (!config?.silent) {
            appStore.requestEnded(config?.request_number ?? 1);
        }

        return Promise.reject(error);
    }
);

function createBackOfficeHttp() {
    const withBackOffice = (config?: CustomAxiosRequestConfig): CustomAxiosRequestConfig => ({
        ...(config ?? {}),
        useBackOffice: true,
    });

    return {
        get: <T = unknown, R = AxiosResponse<T>>(url: string, config?: CustomAxiosRequestConfig) =>
            http.get<T, R>(url, withBackOffice(config)),
        post: <T = unknown, R = AxiosResponse<T>, D = unknown>(
            url: string,
            data?: D,
            config?: CustomAxiosRequestConfig
        ) => http.post<T, R, D>(url, data, withBackOffice(config)),
        put: <T = unknown, R = AxiosResponse<T>, D = unknown>(
            url: string,
            data?: D,
            config?: CustomAxiosRequestConfig
        ) => http.put<T, R, D>(url, data, withBackOffice(config)),
        patch: <T = unknown, R = AxiosResponse<T>, D = unknown>(
            url: string,
            data?: D,
            config?: CustomAxiosRequestConfig
        ) => http.patch<T, R, D>(url, data, withBackOffice(config)),
        delete: <T = unknown, R = AxiosResponse<T>>(
            url: string,
            config?: CustomAxiosRequestConfig
        ) => http.delete<T, R>(url, withBackOffice(config)),
        request: <T = unknown, R = AxiosResponse<T>, D = unknown>(
            config: CustomAxiosRequestConfig
        ) => http.request<T, R, D>(withBackOffice(config)),
    };
}

type BackOfficeHttp = ReturnType<typeof createBackOfficeHttp>;

http.backoffice = createBackOfficeHttp;

export { http, CustomAxiosRequestConfig };
